import { Image } from '@msc-b2c/shared-components';
import { CartBodyLabelsProps, OBSObjI, CartItem } from '@msc-b2c/interfaces/types';
import styles from './card.module.scss';
import { CardDetails } from './CardDetails';
import {
  capitalizeFirstLetterAndLowerCaseRest,
  getActiveSession,
  getActBaseImagePath,
  getActPlaceholderImagePath,
  getDynamicMediaPath,
  getObsBaseImagePath,
  getObsPlaceholderImagePath,
  RootState,
  useAssetAltText,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import { MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { CardFooter } from './CardFooter';

export function Card({
  item,
  labels,
  onEdit,
  onDiningEdit,
  onDelete,
  isPricesLoading,
  relatedItems = [],
  catalogSnapshot,
}: Readonly<{
  item: CartItem;
  labels: Readonly<CartBodyLabelsProps>;
  onEdit?: () => void;
  onDiningEdit?: () => void;
  onDelete?: (itemCode: string, itemDescription: string, startDate?: string) => void;
  isPricesLoading?: boolean;
  /** Linked package lines (e.g. minor ALLINC) merged into this single card. */
  relatedItems?: CartItem[];
  /** Catalog captured before post-purchase OBS refresh; keeps thank-you cards visible. */
  catalogSnapshot?: (OBSObjI | MyMscExcursion)[];
}>) {
  const bookingNumber = getActiveSession().bookingNumber ?? '';
  const { data: bookingData } = useGetBookingQuery(bookingNumber || skipToken, {
    selectFromResult: ({ data }) => ({ data }),
  });

  const obs: OBSObjI[] = useSelector(
    (state: RootState) => state.myCruise?.obs?.onboardServices ?? []
  );
  const excursions: MyMscExcursion[] = useSelector(
    (state: RootState) => state.myCruise?.excursions?.excursions ?? []
  );

  // Combined card: union of passengers across the adult line and its linked package line(s).
  const mergedPassengerIds = [item, ...relatedItems].flatMap(
    (cartItem) => cartItem.passengers?.map((passenger) => passenger.id) ?? []
  );
  const passengers =
    bookingData?.passengers
      ?.filter((passenger) => mergedPassengerIds.includes(passenger.id))
      .map((passenger) =>
        capitalizeFirstLetterAndLowerCaseRest(`${passenger.name} ${passenger.surname}`)
      ) || [];

  const catalog = [...obs, ...excursions];
  const resolveCatalogItem = (code?: string) =>
    catalog.find((candidate) => candidate.code === code) ??
    catalogSnapshot?.find((candidate) => candidate.code === code);

  const service = resolveCatalogItem(item.code);

  // Hook must be called unconditionally (Rules of Hooks) — keep it ABOVE the
  // early return below. Pass the optional code; the hook handles missing input
  // and the consumer falls back to mergedTitle.
  const localizedAssetAlt = useAssetAltText(
    service?.code,
    item.itemType === 'EXCURSION' ? 'act' : 'obs'
  );

  if (!service) {
    return null;
  }

  const relatedServices = relatedItems
    .map((relatedItem) => resolveCatalogItem(relatedItem.code))
    .filter((candidate): candidate is OBSObjI | MyMscExcursion => Boolean(candidate));
  const mergedTitle = [service, ...relatedServices]
    .map((candidate) => candidate.description)
    .filter((description): description is string => Boolean(description))
    .join(', ');

  let placeholderImagePath;
  if (item.itemType === 'EXCURSION') {
    // it's an excursion
    placeholderImagePath = getActPlaceholderImagePath();
  } else {
    // it's an OBS
    placeholderImagePath = getObsPlaceholderImagePath();
  }

  const baseImagePath =
    item.itemType === 'EXCURSION'
      ? getActBaseImagePath() ?? '/content/dam/msc-cruises/b2c-assets/act'
      : getObsBaseImagePath() ?? '/content/dam/msc-cruises/b2c-assets/obs';
  const imageUrl = getDynamicMediaPath(service.code) || `${baseImagePath}/${service.code}.jpg`;

  return (
    <div className={styles['cmp-card']}>
      <div className={styles['cmp-card__container']}>
        <Image
          src={imageUrl}
          alt={localizedAssetAlt || mergedTitle}
          placeholderImagePath={placeholderImagePath}
          styleOption={'card-image'}
        />
        <CardDetails
          item={item}
          labels={labels}
          service={service}
          title={mergedTitle}
          passengers={passengers}
          onEdit={onEdit}
          onDiningEdit={onDiningEdit}
          onDelete={onDelete}
        />
      </div>
      <CardFooter
        item={item}
        labels={labels}
        service={service}
        relatedItems={relatedItems}
        isPricesLoading={isPricesLoading}
      />
    </div>
  );
}
